import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {toggleLoading} from "../../misc/loading.decorator";
import {FindCustomerITSMDialogComponent} from "./project-details-find-customer-dialog.component";
import {Subscription} from "rxjs";
import {ProjectCustomerErrorMessagesDialogComponent} from "../../project/project-customer-error-messages-dialog.component";
import {CustomerOpportunityGroupModel, customerOpportunityGroupModelFactory} from "../../global-deal-settings/customer-opportunity-group.model";
import {GlobalDealSettingsService} from "../../global-deal-settings/global-deal-settings.service";
import {LeadCustomerInfoGqlModel} from "../../global-deal-settings/lead-customer-gpl.model";
import { AppSettingsService } from "../../app-settings/app-settings.service";

export interface FindCustomerOpportunityGroupItsmInputModel {
  customerOpportunityGroupName: string;
  projectId: string;
  targetSystem: string;
  globalOpportunityId: string;
}

export interface FindOpportunityGroupItsmResultsModel {
  selectedCustomerOpportunityGroupInfo: CustomerOpportunityGroupModel;
  customerOpportunityGroupCreated: boolean;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

@Component({
  templateUrl: "./project-details-find-customer-opportunity-group.template.pug",
  providers: [
    GlobalDealSettingsService,
  ],
})
@exposeComponentInDebugMode()
export class FindCustomerOpportunityGroupITSMDialogComponent
  extends DialogComponent<FindCustomerOpportunityGroupItsmInputModel, FindOpportunityGroupItsmResultsModel>
  implements FindCustomerOpportunityGroupItsmInputModel {
  customerOpportunityGroupName: string;
  projectId: string;
  targetSystem: string;
  globalOpportunityId: string;

  error: Error;
  selectedCustomerOpportunityGroup: CustomerOpportunityGroupModel = customerOpportunityGroupModelFactory({});
  // Creation of COG
  customerOpportunityGroupNameInput: string = "";
  leadCustomerName: string = "";
  leadCustomerState: number;
  leadCustomerMessage: string = "";
  leadCustomerErrorMessages: any[];
  leadCustomerSet: boolean = false;
  public enableTaxNumberSearch: boolean;

  isCreateSelected: boolean = false;
  customerOpportunityGroupsList: CustomerOpportunityGroupModel[] = [];
  customerOpportunityGroupsListDisplay: CustomerOpportunityGroupModel[] = [];

  loading: boolean = true;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number = 1;
  totalCount: number = 0;
  matchFound: boolean = false;

  leadCustomerSubscription: Subscription;

  @Input()
  searchText: string = "";

  static showFindOpportunityGroupITSMDialogModal(
    dialogService: DialogService,
    input: FindCustomerOpportunityGroupItsmInputModel,
  ): Promise<FindOpportunityGroupItsmResultsModel> {
    return dialogService.showDialog(FindCustomerOpportunityGroupITSMDialogComponent, input);
  }

  constructor(
    public dialogService: DialogService,
    private globalDealSettingsService: GlobalDealSettingsService,
    private appSettingsService: AppSettingsService,
  ) {
    super(dialogService);

    this.result = {selectedCustomerOpportunityGroupInfo: null, customerOpportunityGroupCreated: false};
  }

  cancel(): void {
    this.close();
  }

  public isDisabledCreateButton(): boolean {
    if (this.globalOpportunityId.length) {
      return this.leadCustomerState !== 2 || !this.leadCustomerSet ||
        !this.customerOpportunityGroupNameInput.length || this.loading;
    }
    return !this.customerOpportunityGroupNameInput.length || this.loading;
  }

  @handleErrors()
  async selectOpportunityGroupComplete() {
    this.result = {
      selectedCustomerOpportunityGroupInfo: {...this.selectedCustomerOpportunityGroup},
      customerOpportunityGroupCreated: false,
    };
    this.close();
  }

  @handleErrors()
  @toggleLoading()
  async createCustomerOpportunityGroup() {
    const createdOpportunityGroup = await this.globalDealSettingsService.createItsmCustomerOpportunityGroup(
      this.customerOpportunityGroupNameInput,
      this.projectId,
    );
    if (createdOpportunityGroup) {
      this.result.selectedCustomerOpportunityGroupInfo = {
        customerOpportunityGroupId: createdOpportunityGroup.customerOpportunityGroupId,
        customerOpportunityGroupName: createdOpportunityGroup.customerOpportunityGroupName,
        leadEndCustomerAccountId: createdOpportunityGroup.leadCustomerItsmId,
      };
      this.result.customerOpportunityGroupCreated = true;
      this.close();
    }
  }

  private adjustListHelper() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.customerOpportunityGroupsListDisplay = (
      this.matchFound &&
      start !== 0 &&
      this.customerOpportunityGroupsList.length
    ) ?
      [
        this.customerOpportunityGroupsList[0],
        ...this.customerOpportunityGroupsList.slice(start, end),
      ] :
      this.customerOpportunityGroupsList.slice(start, end);
  }

  @toggleLoading()
  @handleErrors()
  async loadData() {
    this.enableTaxNumberSearch = await this.appSettingsService.getFeaturePermission("enableTaxNumberSearch", this.projectId);
    const customerOpportunityGroupsListRaw =
      await this.globalDealSettingsService.listCustomerOpportunityGroups({search: this.searchText}, this.projectId, this.targetSystem);
      // Here we'll get the list of available opportunity groups
    this.customerOpportunityGroupsList = customerOpportunityGroupsListRaw.rows;
    this.matchFound = customerOpportunityGroupsListRaw.matchFound;
    this.totalCount = this.customerOpportunityGroupsList.length;
    this.adjustListHelper();
    this.recalculateMaxPages();
    if (this.matchFound || customerOpportunityGroupsListRaw.rows.length === 1) {
      this.selectedCustomerOpportunityGroup = this.customerOpportunityGroupsList[0];
    } else if ( // Current match is not in returned result set
      this.selectedCustomerOpportunityGroup.customerOpportunityGroupId &&
      !this.customerOpportunityGroupsList.find(it =>
        it.customerOpportunityGroupId === this.selectedCustomerOpportunityGroup.customerOpportunityGroupId)
    ) {
      this.selectedCustomerOpportunityGroup = customerOpportunityGroupModelFactory({});
    }
  }

  public leadCustomerSearchBy:string = 'name';

  checkLeadCustomerSearchBy(type:string){
    this.leadCustomerSearchBy = type;
    this.leadCustomerName = '';
  }

  @toggleLoading()
  @handleErrors()
  async ready() {
    this.searchText = this.customerOpportunityGroupName.trim();
    if (this.searchText) {
      await this.loadData();
    }
  }

  public customerOpportunityGroupSelectedEvent(customerOpportunityGroupId) {
    this.selectedCustomerOpportunityGroup =
      this.customerOpportunityGroupsList.find(c => c.customerOpportunityGroupId === customerOpportunityGroupId);
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.totalCount / (+this.pageSize));
  }

  async onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        this.adjustListHelper();
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        this.loading = true;
        await this.loadData();
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        this.adjustListHelper();
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
  }

  changeOpportunitySelected() {
    this.isCreateSelected = !this.isCreateSelected;
    this.error = null;
  }

  showErrorMessages() {
    ProjectCustomerErrorMessagesDialogComponent.showDialog(
      this.dialogService,
      {errorMessages: this.leadCustomerErrorMessages},
    );
  }

  @toggleLoading()
  @handleErrors()
  async searchLeadCustomer() {
    const result = await FindCustomerITSMDialogComponent.showFindCustomerITSMDialogModal(
      this.dialogService,
      {
        customerName: this.leadCustomerName,
        projectId: this.projectId,
        isReseller: false,
        targetSystem: this.targetSystem,
        isLeadCustomer: true,
        customerSearchBy: this.leadCustomerSearchBy,
      },
    );
    if (result.customerSearchBy) {
      this.leadCustomerSearchBy = result.customerSearchBy;
    }
    if(result.customerName){
      this.leadCustomerName = result.customerName;
    }

    if (result && result.selectedCustomerInfo) {
      await this.globalDealSettingsService.setMs4ITSMLeadCustomer(
        this.projectId,
        result.selectedCustomerInfo,
      );
      // Start checking status of LeadCustomer after selecting one.
      this.subscribeLeadCustomer();

      this.leadCustomerName = result.selectedCustomerInfo.customerName;
    }
  }

  private async subscribeLeadCustomer() {
    try {
      const observable = this.globalDealSettingsService.getLeadCustomerStatus(this.projectId);

      if (this.leadCustomerSubscription && !this.leadCustomerSubscription.closed) {
        this.leadCustomerSubscription.unsubscribe();
      }
      await new Promise(() => {
        this.leadCustomerSubscription = observable.subscribe((result: LeadCustomerInfoGqlModel) => {
          this.leadCustomerName = result.leadCustomerName;
          this.leadCustomerState = result.leadCustomerState;
          this.leadCustomerMessage = result.leadCustomerMessage;
          this.leadCustomerErrorMessages = result.leadCustomerErrorMessages;
          if (result.leadCustomerState === 2) this.leadCustomerSet = true;
        });
      }).catch((error) => {
        const errorMessage = (error.length === 1) ?
          (error[0].message || "Unknown Error Message") :
          "Customer / Reseller Error(s)";
        throw new Error(errorMessage);
      });
    } catch (e) {
      throw new Error("An unexpected error occurred trying to get the reseller or customer status");
    }
  }

  ngOnDestroy(): void {
    if (this.leadCustomerSubscription && !this.leadCustomerSubscription.closed) {
      this.leadCustomerSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }
}
